import type { BentoInstance, BentoSettings } from "@bentoapp/types"
import type { Config } from "@ironzion/frontend-js-config"

declare global {
  interface Window {
    Vitally?: {
      init(token: string): void
      // there are more options for the type available in Vitally's docs at
      // https://docs.vitally.io/pushing-data-to-vitally/using-the-vitally-api/vitallys-javascript-sdk-vitally.js,
      // but they don't seem to make TS defs available.
      nps(survey: "survey", options: { autoLoadSegment: boolean; productName: string }): void
      // add these if/when TS asks for them:
      // user
      // account
      // track
    }

    ChiliPiper?: {
      submit(...args: unknown[]): void
    }

    // as in other tags we use, there may be more overloads or methods here.
    // Add as needed!
    Intercom?(method: "boot", params: Record<string, unknown>): void
    Intercom?(method: "shutdown"): void
    Intercom?(method: "showSpace", space: string): void
    Intercom?(method: "showNewMessage", message?: string): void

    bentoSettings?: BentoSettings
    Bento?: BentoInstance

    // only typing the parts we need here. Add more as needed!
    // https://docs.appcues.com/en_US/dev-api-data/javascript-api-developer
    Appcues?: {
      show(contentId: string): void
    }

    // merge.dev does not provide reliable typings for their tag. In addition,
    // the `filePickerConfig` appears to be undocumented! (We learned about it
    // by talking to them.) Like other tags in this file, these types are based
    // on having successfully called the API with these data shapes, and if we
    // get the chance to upgrade to something provided by Merge instead, we
    // should do that.
    MergeLink?: {
      initialize(config: {
        linkToken: string
        onReady?: () => void
        onSuccess?: (publicToken: string) => void
        onExit?: () => void
        filePickerConfig?: {
          allowMultiSelect: boolean
          types: string[]
          onSubmit: (outputObjects: { id?: string; name?: string }[]) => void
        }
      }): void

      openLink(): void
    }
  }
}

/**
 * Setup an onLoad event for our FE Vitally SDK
 * This SDK is loaded from index.html in a script tag
 *
 */
export const configureVitally = (constants: Config) => {
  // Instert the SDK listed here: https://docs.vitally.io/pushing-data-to-vitally/using-the-vitally-api/vitallys-javascript-sdk-vitally.js
  const e = document.createElement("script")
  e.type = "text/javascript"
  e.async = !0
  e.defer = !0
  e.src = ("https:" === document.location.protocol ? "https://" : "http://") + "cdn.vitally.io/vitally.js/v1/vitally.js"
  const n = document.getElementsByTagName("script")[0]
  if (!n || !n.parentNode) {
    throw "could not initialize vitally. No attached script nodes found."
  }
  n.parentNode.insertBefore(e, n)
  // Initialize: copied from https://docs.vitally.io/pushing-data-to-vitally/using-the-vitally-api/vitallys-javascript-sdk-vitally.js
  //
  // Because this is a third-party shim script, we ignore errors it contains.

  /* @ts-ignore */
  !(function (n: window, t: string) {
    for (
      let i = (n[t] = n[t] || []),
        /* @ts-ignore */
        o = function (r) {
          i[r] =
            i[r] ||
            function () {
              for (let n = [], t = 0; t < arguments.length; t++) {
                n[t] = arguments[t] // eslint-disable-line prefer-rest-params
              }
              return i.push([r, n])
            }
        },
        u = 0,
        c = ["init", "user", "account", "track", "nps"];
      u < c.length;
      u++
    ) {
      o(c[u])
    }
  })(window, "Vitally")

  if (!window.Vitally) {
    throw "Vitally wasn't defined after the completion of the shim script"
  }

  window.Vitally.init(constants.vitallyToken)
}
